class PartnersSpotlight {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.container = this.section.querySelector('.partners-spotlight-slider-container');
    this.slider = this.section.querySelector('.partners-spotlight-slider');
    this.slides = Array.from(this.slider.querySelectorAll('.partners-spotlight-slide:not(.partners-spotlight-slide--placeholder)'));
    this.prevButton = this.section.querySelector('.partners-spotlight-navigation .button.prev');
    this.nextButton = this.section.querySelector('.partners-spotlight-navigation .button.next');

    if (!this.slider || !this.prevButton || !this.nextButton) {
      console.warn('Partners Spotlight: Essential elements (slider, prev/next buttons) not found.');
      return;
    }
    
    if (this.slides.length === 0) {
      if(this.prevButton) this.prevButton.style.display = 'none';
      if(this.nextButton) this.nextButton.style.display = 'none';
      return; // No slides to operate on (excluding placeholders)
    }

    this.settings = {
      slidesToShowDesktop: parseInt(this.section.dataset.slidesToShow) || 3,
      autoplay: this.section.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(this.section.dataset.autoplaySpeed) || 5000,
      pauseOnHover: this.section.dataset.pauseOnHover === 'true'
    };

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.slidesToShowEffective = this.settings.slidesToShowDesktop;
    this.slideWidth = 0;
    this.slideGap = parseFloat(getComputedStyle(this.slider).gap) || 20;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;
    this.autoplayInterval = null;
    this.isTransitioning = false; // To prevent rapid clicks during animation

    this.init();
  }

  init() {
    this.createLiveRegion();
    this.updateSlidesToShow();
    this.calculateSlideDimensions();
    this.slider.style.setProperty('--slides-to-show', this.slidesToShowEffective);
    this.applyTransformation();
    this.bindEvents();
    this.updateButtonStates();
    this.setSlideAccessibility();
    this.addLoadingAttributeSupport();

    if (this.settings.autoplay && this.totalSlides > this.slidesToShowEffective) {
      this.startAutoplay();
    }
  }
  
  createLiveRegion() {
    // Live region removed to prevent visual display of slide announcements
  }

  announceSlideChange() {
    // Slide announcement removed
  }

  updateSlidesToShow() {
    if (window.innerWidth <= 749) { // Mobile
      this.slidesToShowEffective = 1;
    } else if (window.innerWidth <= 990) { // Tablet
      this.slidesToShowEffective = 2;
    } else { // Desktop
      this.slidesToShowEffective = this.settings.slidesToShowDesktop;
    }
    this.slidesToShowEffective = Math.min(this.slidesToShowEffective, this.totalSlides);
  }

  calculateSlideDimensions() {
    if (this.slides.length === 0) return;
    const sliderVisibleWidth = this.container.offsetWidth;
    this.slideGap = parseFloat(getComputedStyle(this.slider).gap) || 20;

    this.slideWidth = (sliderVisibleWidth - (this.slideGap * (this.slidesToShowEffective - 1))) / this.slidesToShowEffective;
    
    this.slides.forEach(slide => {
      slide.style.width = `${this.slideWidth}px`;
    });
    this.applyTransformation();
    this.updateButtonStates();
  }

  bindEvents() {
    this.prevButton.addEventListener('click', () => this.changeSlide('prev'));
    this.nextButton.addEventListener('click', () => this.changeSlide('next'));

    this.slider.addEventListener('transitionend', () => {
        this.isTransitioning = false;
    });

    // Touch events
    this.slider.addEventListener('touchstart', (e) => this.touchStart(e), { passive: true });
    this.slider.addEventListener('touchmove', (e) => this.touchMove(e), { passive: true });
    this.slider.addEventListener('touchend', () => this.touchEnd());

    // Mouse events (for dragging)
    this.slider.addEventListener('mousedown', (e) => this.touchStart(e));
    this.slider.addEventListener('mousemove', (e) => this.touchMove(e));
    this.slider.addEventListener('mouseup', () => this.touchEnd());
    this.slider.addEventListener('mouseleave', () => this.touchEnd(true));

    // Debounced resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateSlidesToShow();
        this.calculateSlideDimensions();
      }, 250);
    });

    if (this.settings.pauseOnHover && this.settings.autoplay) {
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => {
        if (this.totalSlides > this.slidesToShowEffective) this.startAutoplay();
      });
    }
    
    this.container.addEventListener('focusin', () => this.stopAutoplay());
    this.container.addEventListener('focusout', () => {
        if (this.settings.pauseOnHover && this.settings.autoplay && this.totalSlides > this.slidesToShowEffective) {
            // Check if focus is still within the slider container or its children
            if (!this.container.contains(document.activeElement)) {
                this.startAutoplay();
            }
        }
    });

    // Keyboard navigation for the container (when focused)
    this.container.addEventListener('keydown', (e) => {
      if (e.target.closest('.partners-spotlight-nav-arrow')) return; // Let button handle its own keydown
      let handled = false;
      if (e.key === 'ArrowLeft') {
        this.changeSlide('prev');
        handled = true;
      }
      if (e.key === 'ArrowRight') {
        this.changeSlide('next');
        handled = true;
      }
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  changeSlide(direction) {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const oldIndex = this.currentIndex;
    if (direction === 'next') {
      this.currentIndex = Math.min(this.currentIndex + 1, this.totalSlides - this.slidesToShowEffective);
    } else if (direction === 'prev') {
      this.currentIndex = Math.max(this.currentIndex - 1, 0);
    }
    
    if (oldIndex !== this.currentIndex) {
        this.applyTransformation();
        this.updateButtonStates();
        this.setSlideAccessibility();
        this.announceSlideChange();
    } else {
        this.isTransitioning = false; // No change, reset flag
    }
    this.resetAutoplay();
  }
  
  goToSlide(index) {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const oldIndex = this.currentIndex;
    const maxIndex = Math.max(0, this.totalSlides - this.slidesToShowEffective);
    this.currentIndex = Math.min(Math.max(index, 0), maxIndex);

    if (oldIndex !== this.currentIndex) {
        this.applyTransformation();
        this.updateButtonStates();
        this.setSlideAccessibility();
        this.announceSlideChange();
    } else {
        this.isTransitioning = false;
    }
    this.resetAutoplay();
  }

  applyTransformation() {
    if (this.slides.length === 0) return;
    const totalSlideWidthAndGap = this.slideWidth + this.slideGap;
    this.currentTranslate = -this.currentIndex * totalSlideWidthAndGap;
    this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  updateButtonStates() {
    const maxIndex = this.totalSlides - this.slidesToShowEffective;
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex >= maxIndex;
    
    if (this.totalSlides <= this.slidesToShowEffective) {
        this.prevButton.style.visibility = 'hidden';
        this.nextButton.style.visibility = 'hidden';
    } else {
        this.prevButton.style.visibility = 'visible';
        this.nextButton.style.visibility = 'visible';
    }
  }
  
  setSlideAccessibility() {
    this.slides.forEach((slide, index) => {
      const isVisible = index >= this.currentIndex && index < this.currentIndex + this.slidesToShowEffective;
      slide.setAttribute('aria-hidden', !isVisible);
      slide.querySelectorAll('a, button').forEach(focusableElement => {
        focusableElement.tabIndex = isVisible ? 0 : -1;
      });
    });
  }

  startAutoplay() {
    if (!this.settings.autoplay || this.totalSlides <= this.slidesToShowEffective) return;
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      let nextIndex = this.currentIndex + 1;
      if (nextIndex > this.totalSlides - this.slidesToShowEffective) {
        nextIndex = 0; // Loop back to start
      }
      this.goToSlide(nextIndex);
    }, this.settings.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayInterval);
    this.autoplayInterval = null;
  }

  resetAutoplay() {
    if (this.settings.autoplay && this.totalSlides > this.slidesToShowEffective) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  touchStart(event) {
    if (this.isTransitioning) return;
    this.startX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
    this.isDragging = true;
    this.prevTranslate = this.currentTranslate;
    this.slider.style.transition = 'none';
    this.animationID = requestAnimationFrame(() => this.dragAnimation());
    this.stopAutoplay();
  }

  touchMove(event) {
    if (!this.isDragging) return;
    const currentX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
    const diffX = currentX - this.startX;
    // Limit dragging beyond first/last slide by a certain amount for a 'bounce' effect feel, but snap back
    const dragLimit = this.slideWidth / 2;
    if (this.prevTranslate + diffX > dragLimit) {
        this.currentTranslate = dragLimit;
    } else if (this.prevTranslate + diffX < -( (this.totalSlides - this.slidesToShowEffective) * (this.slideWidth + this.slideGap) + dragLimit) ) {
        this.currentTranslate = -( (this.totalSlides - this.slidesToShowEffective) * (this.slideWidth + this.slideGap) + dragLimit);
    } else {
        this.currentTranslate = this.prevTranslate + diffX;
    }
  }
  
  dragAnimation() {
      if(this.isDragging) {
          this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
          requestAnimationFrame(() => this.dragAnimation());
      }
  }

  touchEnd(mouseLeave = false) {
    if (!this.isDragging) return;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.slider.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)';

    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = this.slideWidth / 3;

    if (mouseLeave && Math.abs(movedBy) < 5) { // If mouse left and very little drag, snap back & reset autoplay
         this.applyTransformation(); // Snap back
         this.resetAutoplay();
         return;
    }

    if (movedBy < -threshold) {
      this.changeSlide('next');
    } else if (movedBy > threshold) {
      this.changeSlide('prev');
    } else {
      this.applyTransformation(); // Snap back if not moved enough
    }
    // Autoplay is reset within changeSlide, if no change, reset it here
    if(Math.abs(movedBy) <= threshold) this.resetAutoplay(); 
  }
  
  addLoadingAttributeSupport() {
      this.slides.forEach(slide => {
          const link = slide.querySelector('.partners-spotlight-card-link');
          const images = slide.querySelectorAll('.partners-spotlight-background-image, .partners-spotlight-logo-image');
          if (link && images.length > 0) {
              let imagesLoaded = 0;
              link.dataset.loading = 'true'; // Set loading state
              images.forEach(img => {
                  if (img.complete) {
                      imagesLoaded++;
                  } else {
                      img.onload = () => {
                          imagesLoaded++;
                          if (imagesLoaded === images.length) {
                              delete link.dataset.loading; // Remove loading state
                          }
                      };
                      img.onerror = () => { // Handle error case
                          imagesLoaded++;
                          if (imagesLoaded === images.length) {
                              delete link.dataset.loading;
                          }
                          // Potentially add an error class or different placeholder
                      };
                  }
              });
              if (imagesLoaded === images.length) {
                 delete link.dataset.loading;
              }
          }
      });
  }
}

function initializePartnersSpotlight() {
    document.querySelectorAll('.shopify-section-partners-spotlight').forEach(sectionElement => {
        // Prevent re-initialization
        if (!sectionElement.partnersSpotlightInstance) {
            sectionElement.partnersSpotlightInstance = new PartnersSpotlight(sectionElement);
        }
    });
}

document.addEventListener('DOMContentLoaded', initializePartnersSpotlight);
document.addEventListener('shopify:section:load', (event) => {
    const sectionElement = event.target.closest('.shopify-section-partners-spotlight');
    if (sectionElement) {
        // If an instance already exists (e.g., from DOMContentLoaded), destroy it or re-init carefully.
        // For simplicity here, we assume Shopify might re-run scripts causing issues, so we check.
        if (sectionElement.partnersSpotlightInstance && typeof sectionElement.partnersSpotlightInstance.destroy === 'function') {
           // sectionElement.partnersSpotlightInstance.destroy(); // Implement a destroy method if needed
        }
        sectionElement.partnersSpotlightInstance = new PartnersSpotlight(sectionElement);
    }
});
document.addEventListener('shopify:section:unload', (event) => {
    const sectionElement = event.target.closest('.shopify-section-partners-spotlight');
    if (sectionElement && sectionElement.partnersSpotlightInstance) {
        if (typeof sectionElement.partnersSpotlightInstance.stopAutoplay === 'function') {
            sectionElement.partnersSpotlightInstance.stopAutoplay();
        }
        // Could add a more formal destroy method to remove all event listeners if needed
        delete sectionElement.partnersSpotlightInstance;
    }
}); 